import React from 'react'

import { FormHelperText, TableCell, TableRow, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'
import Editor from '@scoir/editor'
import { ErrorBoundary } from '@scoir/error-reporter'
import { cloneDeep } from 'lodash'
import PropTypes from 'prop-types'

import sanitize from 'common/sanitize-html-helper'

import SectionTable from './components/SectionTable'
import { getCharCount } from './textUtilities'

const useStyles = makeStyles((theme) => ({
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  charCount: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  loadingBox: {
    '& .MuiSkeleton-text': {
      transform: 'unset',
    },
  },
  thirdLevelHeading: {
    color: theme.palette.text.secondary,
    paddingBottom: 30,
  },
}))

const maxNoteLength = 2400
const essayText =
  'Please list any essay prompts applicants will be presented with on your supplement. If certain essays are only required for applicants meeting specific criteria, please note that as well.'

const SupplementalEssay = ({ isEditing, applicationConfig, setApplicationConfig, loading }) => {
  const classes = useStyles()
  const onChange = (sanitizedVal) => {
    const updatedState = cloneDeep(applicationConfig)
    const isError = validateInput(sanitizedVal)

    const essayConfig = updatedState.supplementEssayInfo ? updatedState.supplementEssayInfo : {}
    essayConfig.body = sanitizedVal
    updatedState.supplementEssayInfo = { ...essayConfig }

    setApplicationConfig(updatedState, isError)
  }

  const validateInput = (val) => {
    const count = getCharCount(val)
    return count > maxNoteLength
  }

  const initialVal =
    applicationConfig.supplementEssayInfo && applicationConfig.supplementEssayInfo.body
      ? applicationConfig.supplementEssayInfo.body
      : ''

  const isError = validateInput(sanitize(initialVal))

  return (
    <SectionTable
      sectionName="Supplement Essay Prompts"
      onlyShowLabel={true}
    >
      <TableRow>
        {loading ? (
          <TableCell>
            <Skeleton
              data-testid="loader-test-id"
              className={classes.loadingBox}
              height={337}
            />
          </TableCell>
        ) : isEditing ? (
          <TableCell>
            <Typography
              variant="body2"
              className={classes.thirdLevelHeading}
            >
              {essayText}
            </Typography>
            <ErrorBoundary>
              <Editor
                value={initialVal}
                onChange={(html) => onChange(sanitize(html))}
              />
            </ErrorBoundary>

            <FormHelperText
              error={isError}
              className={classes.charCount}
              role={isError ? 'alert' : undefined}
              aria-live="polite"
            >
              {maxNoteLength - getCharCount(initialVal)} characters remaining
            </FormHelperText>
          </TableCell>
        ) : (
          <TableCell>
            <Typography
              variant="body2"
              className={classes.thirdLevelHeading}
            >
              {essayText}
            </Typography>
            {initialVal ? (
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{
                  __html: sanitize(initialVal),
                }}
              ></Typography>
            ) : (
              <Typography
                variant="body2"
                className={classes.secondaryText}
              >
                None
              </Typography>
            )}
          </TableCell>
        )}
      </TableRow>
    </SectionTable>
  )
}

SupplementalEssay.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  applicationConfig: PropTypes.object.isRequired,
  setApplicationConfig: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
}

export default SupplementalEssay
